<template>
  <div class="flowchart-editor">
    <div class="editor-content">
      <!-- 左侧面板 -->
      <div class="element-library">
        <div class="panel-header">
          <div class="panel-title">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="panel-icon">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
            </svg>
            <span>流程图</span>
          </div>
          <button class="btn-history" @click="showHistoryDialog">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            历史流程图
          </button>
        </div>
        <div class="save-reminder">
          💡 提示：AI生成成功后会自动保存流程图。
        </div>

        <div class="ai-form-panel">
          <el-form :model="aiForm" label-position="top">
            <el-form-item label="课题名称">
              <el-input v-model="aiForm.projectName" placeholder="例如：用户登录流程" clearable />
            </el-form-item>
            <el-form-item label="流程描述">
              <textarea
                v-model="aiForm.flowDescription"
                class="func-editor"
                placeholder="请描述流程的主要步骤，例如：

1. 用户打开登录页面
2. 输入账号和密码
3. 系统验证账号密码
4. 验证成功则跳转首页
5. 验证失败则提示错误并重试"
              />
            </el-form-item>
          </el-form>
        </div>

        <div class="panel-footer">
          <button class="btn-ai btn-generate" @click="submitAIGenerate">
            <span class="btn-content">
              <img src="@/assets/ai-logo.png" alt="AI" class="ai-icon" />
              AI流程图
            </span>
            <span class="btn-points">（消耗{{ pointsConfig.flowchart }}积分）</span>
          </button>
        </div>
      </div>

      <!-- 右侧画布区域 -->
      <div class="canvas-area">
        <div class="tabs-container">
          <div class="tabs">
            <button class="tab active">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
              </svg>
              流程图画布
            </button>
          </div>
          <div class="canvas-actions">
            <button class="tool-btn btn-green" @click="zoomIn" title="放大">
              <i class="el-icon-zoom-in"></i>放大
            </button>
            <button class="tool-btn btn-teal" @click="zoomOut" title="缩小">
              <i class="el-icon-zoom-out"></i>缩小
            </button>
            <button class="tool-btn btn-orange" @click="clearCanvas" title="清空画布">
              <i class="el-icon-delete"></i>清空画布
            </button>
            <button class="tool-btn btn-blue" @click="openExportDialog" title="导出流程图">
              <i class="el-icon-download"></i>导出流程图
            </button>
            <button class="tool-btn btn-purple" @click="toggleFullscreen" :title="isFullscreen ? '退出全屏' : '全屏查看'">
              <i class="el-icon-full-screen"></i>{{ isFullscreen ? '退出全屏' : '全屏' }}
            </button>
          </div>
        </div>

        <div class="toolbar">
          <div class="tip-banner">
            💡 提示：可导出为 Draw.io 文件进行进一步编辑和美化，支持全屏模式获得更大编辑空间。
          </div>
        </div>

        <!-- 参数设置 -->
        <div class="params-setting">
          <div class="param-item">
            <label>字体:</label>
            <el-select v-model="fontFamily" size="small" style="width: 100px;" @change="updateFont">
              <el-option label="宋体" value="SimSun"></el-option>
              <el-option label="微软雅黑" value="Microsoft YaHei"></el-option>
              <el-option label="黑体" value="SimHei"></el-option>
              <el-option label="仿宋" value="FangSong"></el-option>
              <el-option label="楷体" value="KaiTi"></el-option>
              <el-option label="Arial" value="Arial"></el-option>
            </el-select>
          </div>
          <div class="param-item">
            <label>字体大小:</label>
            <el-select v-model="fontSize" size="small" style="width: 80px;" @change="updateFont">
              <el-option label="10px" :value="10"></el-option>
              <el-option label="12px" :value="12"></el-option>
              <el-option label="14px" :value="14"></el-option>
              <el-option label="16px" :value="16"></el-option>
              <el-option label="18px" :value="18"></el-option>
              <el-option label="20px" :value="20"></el-option>
              <el-option label="22px" :value="22"></el-option>
            </el-select>
          </div>
          <div class="param-item">
            <label>字体加粗:</label>
            <el-select v-model="fontWeight" size="small" style="width: 80px;" @change="updateFont">
              <el-option label="正常" value="normal"></el-option>
              <el-option label="加粗" value="bold"></el-option>
            </el-select>
          </div>

          <div class="param-item">
            <label>线条宽度:</label>
            <div class="number-input">
              <el-button size="small" @click="decreaseLineWidth">-</el-button>
              <span class="number-value">{{ lineWidth }}</span>
              <el-button size="small" @click="increaseLineWidth">+</el-button>
            </div>
          </div>
          <div class="param-item">
            <label>箭头尺寸:</label>
            <el-select v-model="arrowSize" size="small" style="width: 80px;" @change="updateArrowSize">
              <el-option label="无" value="none"></el-option>
              <el-option label="小" value="small"></el-option>
              <el-option label="中" value="medium"></el-option>
              <el-option label="大" value="large"></el-option>
            </el-select>
          </div>
        </div>

        <div class="canvas-content">
          <div class="canvas" @click="onCanvasClick">
            <svg
              ref="diagramSvg"
              width="100%"
              height="100%"
              class="diagram-svg"
              :viewBox="computedViewBox"
              @mousedown="onCanvasMouseDown"
              @mousemove="onCanvasMouseMove"
              @mouseup="onCanvasMouseUp"
              @mouseleave="onCanvasMouseUp"
              :class="{ 'panning': isPanning }"
            >
              <defs>
                <marker id="arrowhead" :markerWidth="arrowDimensions.width" :markerHeight="arrowDimensions.height"
                        :refX="arrowDimensions.refX" :refY="arrowDimensions.refY" orient="auto">
                  <polygon :points="arrowDimensions.points" :fill="lineColor" />
                </marker>
                <marker id="hollow-arrowhead" :markerWidth="hollowArrowDimensions.width" :markerHeight="hollowArrowDimensions.height"
                        :refX="hollowArrowDimensions.refX" :refY="hollowArrowDimensions.refY" orient="auto">
                  <polygon :points="hollowArrowDimensions.points" fill="white" :stroke="lineColor" :stroke-width="lineWidth" />
                </marker>
              </defs>

              <!-- 渲染系统边界 -->
              <g class="boundaries-layer">
                <g v-for="boundary in boundaries" :key="boundary.id" class="boundary"
                   :class="{ selected: selectedElement && selectedElement.id === boundary.id }"
                   @mousedown.stop="onElementMouseDown(boundary, $event)">
                  <rect :x="boundary.x - boundary.width/2" :y="boundary.y - boundary.height/2"
                        :width="boundary.width" :height="boundary.height" fill="transparent" class="drag-area"/>
                  <rect v-if="selectedElement && selectedElement.id === boundary.id"
                        :x="boundary.x - boundary.width/2 - 5" :y="boundary.y - boundary.height/2 - 5"
                        :width="boundary.width + 10" :height="boundary.height + 10"
                        fill="none" stroke="#1890ff" stroke-width="2" stroke-dasharray="5,5"/>
                  <rect :x="boundary.x - boundary.width/2" :y="boundary.y - boundary.height/2"
                        :width="boundary.width" :height="boundary.height"
                        fill="none" :stroke="lineColor" :stroke-width="lineWidth"
                        :stroke-dasharray="boundary.isDashed ? '8,4' : 'none'"/>
                  <g v-if="!(editingElement && editingElement.id === boundary.id)" class="boundary-text-group">
                    <rect :x="boundary.textX - 60" :y="boundary.textY - 12" width="120" height="24"
                          fill="transparent" class="text-drag-area" @mousedown.stop="startTextDrag(boundary, $event)"/>
                    <rect v-if="selectedElement && selectedElement.id === boundary.id && !isDraggingText"
                          :x="boundary.textX - 62" :y="boundary.textY - 14" width="124" height="28"
                          fill="rgba(82,196,26,0.1)" stroke="#52c41a" stroke-width="1" stroke-dasharray="3,3" class="text-drag-hint"/>
                    <text :x="boundary.textX" :y="boundary.textY" text-anchor="middle" class="boundary-label"
                          :class="{ 'text-dragging': isDraggingText && selectedElement && selectedElement.id === boundary.id }"
                          :style="dynamicFontStyle" @click.stop="startEdit(boundary)" @mousedown.stop="startTextDrag(boundary, $event)">
                      {{ boundary.name }}
                    </text>
                  </g>
                  <foreignObject v-if="editingElement && editingElement.id === boundary.id"
                                 :x="boundary.textX - 80" :y="boundary.textY - 15" width="160" height="30">
                    <input :ref="'editInput_' + boundary.id" v-model="editingText"
                           @keydown.enter="saveEdit" @keydown.esc="cancelEdit"
                           @mousedown.stop @click.stop class="inline-edit-input" type="text"/>
                  </foreignObject>
                  <circle v-if="selectedElement && selectedElement.id === boundary.id"
                          :cx="boundary.x + boundary.width/2 + 10" :cy="boundary.y - boundary.height/2 - 10"
                          r="8" fill="#ff4d4f" stroke="white" stroke-width="2" class="delete-btn"
                          @click.stop="deleteElement(boundary)"/>
                  <text v-if="selectedElement && selectedElement.id === boundary.id"
                        :x="boundary.x + boundary.width/2 + 10" :y="boundary.y - boundary.height/2 - 6"
                        text-anchor="middle" fill="white" font-size="12" class="delete-btn"
                        @click.stop="deleteElement(boundary)">×</text>
                  <g v-if="selectedElement && selectedElement.id === boundary.id" class="resize-handles">
                    <circle :cx="boundary.x + boundary.width/2" :cy="boundary.y" r="4"
                            fill="#1890ff" stroke="white" stroke-width="2" class="resize-handle resize-width"
                            @mousedown.stop="onResizeStart(boundary, $event, 'width')"/>
                    <circle :cx="boundary.x" :cy="boundary.y + boundary.height/2" r="4"
                            fill="#1890ff" stroke="white" stroke-width="2" class="resize-handle resize-height"
                            @mousedown.stop="onResizeStart(boundary, $event, 'height')"/>
                    <circle :cx="boundary.x + boundary.width/2" :cy="boundary.y + boundary.height/2" r="4"
                            fill="#52c41a" stroke="white" stroke-width="2" class="resize-handle resize-both"
                            @mousedown.stop="onResizeStart(boundary, $event, 'both')"/>
                  </g>
                </g>
              </g>

              <!-- 渲染关系线 -->
              <g class="relationships-layer">
                <g v-for="rel in relationships" :key="rel.id" class="relationship"
                   @mouseenter="setHoveredLine(rel)" @mouseleave="clearHoveredLine()">
                  <!-- 折线（有 waypoints），用 path 支持箭头 -->
                  <path v-if="rel.waypoints && rel.waypoints.length"
                        :d="getPolylinePath(rel)"
                        :stroke="lineColor" :stroke-width="lineWidth"
                        :marker-end="getMarkerEnd(rel)"
                        :stroke-dasharray="rel.isDashed ? '5,5' : 'none'"
                        fill="none" class="line-main" @click="selectLine(rel)"/>
                  <path v-if="rel.waypoints && rel.waypoints.length"
                        :d="getPolylinePath(rel)"
                        stroke="transparent" stroke-width="15" fill="none" class="line-drag-area"
                        @mousedown.stop="startLineDrag(rel, $event)" @dblclick.stop="startEdit(rel)"/>
                  <!-- 直线 -->
                  <line v-if="!rel.waypoints || !rel.waypoints.length"
                        :x1="rel.x1" :y1="rel.y1" :x2="rel.x2" :y2="rel.y2"
                        :stroke="lineColor" :stroke-width="lineWidth" :marker-end="getMarkerEnd(rel)"
                        :stroke-dasharray="rel.isDashed ? '5,5' : 'none'" class="line-main" @click="selectLine(rel)"/>
                  <line v-if="!rel.waypoints || !rel.waypoints.length"
                        :x1="rel.x1" :y1="rel.y1" :x2="rel.x2" :y2="rel.y2"
                        stroke="transparent" stroke-width="15" class="line-drag-area"
                        @mousedown.stop="startLineDrag(rel, $event)" @dblclick.stop="startEdit(rel)"/>
                  <g v-if="(selectedElement && selectedElement.id === rel.id) || (hoveredLine && hoveredLine.id === rel.id)">
                    <circle :cx="rel.x1" :cy="rel.y1" r="5" fill="#52c41a" stroke="white" stroke-width="2"
                            class="endpoint-handle start-point" @mousedown.stop="startEndpointDrag(rel, 'start', $event)"/>
                    <circle :cx="rel.x2" :cy="rel.y2" r="5" fill="#1890ff" stroke="white" stroke-width="2"
                            class="endpoint-handle end-point" @mousedown.stop="startEndpointDrag(rel, 'end', $event)"/>
                  </g>
                  <!-- 标签：折线显示在第一段中点，直线显示在中点 -->
                  <text v-if="!(editingElement && editingElement.id === rel.id) && rel.label && rel.label.trim()"
                        :x="getLabelX(rel)" :y="getLabelY(rel)"
                        text-anchor="middle" class="relationship-label" :style="dynamicFontStyle" @click.stop="startEdit(rel)">
                    {{ rel.label }}
                  </text>
                  <foreignObject v-if="editingElement && editingElement.id === rel.id"
                                 :x="(rel.x1 + rel.x2) / 2 - 50" :y="(rel.y1 + rel.y2) / 2 - 20" width="100" height="30">
                    <input :ref="'editInput_' + rel.id" v-model="editingText"
                           @keydown.enter="saveEdit" @keydown.esc="cancelEdit"
                           @mousedown.stop @click.stop class="inline-edit-input" type="text"/>
                  </foreignObject>
                  <circle v-if="selectedElement && selectedElement.id === rel.id"
                          :cx="(rel.x1 + rel.x2) / 2 + 30" :cy="(rel.y1 + rel.y2) / 2 - 20"
                          r="8" fill="#ff4d4f" stroke="white" stroke-width="2" class="delete-btn"
                          @click.stop="deleteElement(rel)"/>
                  <text v-if="selectedElement && selectedElement.id === rel.id"
                        :x="(rel.x1 + rel.x2) / 2 + 30" :y="(rel.y1 + rel.y2) / 2 - 16"
                        text-anchor="middle" fill="white" font-size="12" class="delete-btn"
                        @click.stop="deleteElement(rel)">×</text>
                  <circle v-if="selectedElement && selectedElement.id === rel.id && rel.label && rel.label.trim()"
                          :cx="(rel.x1 + rel.x2) / 2 - 30" :cy="(rel.y1 + rel.y2) / 2 - 20"
                          r="8" fill="#faad14" stroke="white" stroke-width="2" class="delete-text-btn"
                          @click.stop="deleteText(rel)"/>
                  <text v-if="selectedElement && selectedElement.id === rel.id && rel.label && rel.label.trim()"
                        :x="(rel.x1 + rel.x2) / 2 - 30" :y="(rel.y1 + rel.y2) / 2 - 16"
                        text-anchor="middle" fill="white" font-size="10" class="delete-text-btn"
                        @click.stop="deleteText(rel)">T×</text>
                </g>
              </g>

              <!-- 渲染节点 -->
              <g class="nodes-layer">
                <g v-for="node in nodes" :key="node.id" class="flow-node"
                   :class="{ selected: selectedElement && selectedElement.id === node.id }"
                   @mousedown.stop="onElementMouseDown(node, $event)">
                  <!-- 圆角矩形（开始/结束） -->
                  <template v-if="node.shape === 'terminator'">
                    <rect :x="node.x - node.width/2" :y="node.y - node.height/2"
                          :width="node.width" :height="node.height" fill="transparent" class="drag-area"/>
                    <rect v-if="selectedElement && selectedElement.id === node.id"
                          :x="node.x - node.width/2 - 4" :y="node.y - node.height/2 - 4"
                          :width="node.width + 8" :height="node.height + 8"
                          fill="none" stroke="#1890ff" stroke-width="2" stroke-dasharray="5,5" rx="4"/>
                    <rect :x="node.x - node.width/2" :y="node.y - node.height/2"
                          :width="node.width" :height="node.height"
                          :rx="node.height/2" fill="white" :stroke="lineColor" :stroke-width="lineWidth"/>
                  </template>
                  <!-- 平行四边形（输入/输出） -->
                  <template v-else-if="node.shape === 'parallelogram'">
                    <polygon :points="parallelogramPoints(node)" fill="transparent" class="drag-area"/>
                    <rect v-if="selectedElement && selectedElement.id === node.id"
                          :x="node.x - node.width/2 - 4" :y="node.y - node.height/2 - 4"
                          :width="node.width + 8" :height="node.height + 8"
                          fill="none" stroke="#1890ff" stroke-width="2" stroke-dasharray="5,5"/>
                    <polygon :points="parallelogramPoints(node)"
                             fill="white" :stroke="lineColor" :stroke-width="lineWidth"/>
                  </template>
                  <!-- 矩形节点 -->
                  <template v-else-if="node.shape === 'rect'">
                    <rect :x="node.x - node.width/2" :y="node.y - node.height/2"
                          :width="node.width" :height="node.height" fill="transparent" class="drag-area"/>
                    <rect v-if="selectedElement && selectedElement.id === node.id"
                          :x="node.x - node.width/2 - 4" :y="node.y - node.height/2 - 4"
                          :width="node.width + 8" :height="node.height + 8"
                          fill="none" stroke="#1890ff" stroke-width="2" stroke-dasharray="5,5"/>
                    <rect :x="node.x - node.width/2" :y="node.y - node.height/2"
                          :width="node.width" :height="node.height"
                          fill="white" :stroke="lineColor" :stroke-width="lineWidth"/>
                  </template>
                  <!-- 菱形节点 -->
                  <template v-else-if="node.shape === 'diamond'">
                    <polygon :points="`${node.x},${node.y - node.height/2} ${node.x + node.width/2},${node.y} ${node.x},${node.y + node.height/2} ${node.x - node.width/2},${node.y}`"
                             fill="transparent" class="drag-area"/>
                    <polygon v-if="selectedElement && selectedElement.id === node.id"
                             :points="`${node.x},${node.y - node.height/2 - 5} ${node.x + node.width/2 + 5},${node.y} ${node.x},${node.y + node.height/2 + 5} ${node.x - node.width/2 - 5},${node.y}`"
                             fill="none" stroke="#1890ff" stroke-width="2" stroke-dasharray="5,5"/>
                    <polygon :points="`${node.x},${node.y - node.height/2} ${node.x + node.width/2},${node.y} ${node.x},${node.y + node.height/2} ${node.x - node.width/2},${node.y}`"
                             fill="white" :stroke="lineColor" :stroke-width="lineWidth"/>
                  </template>
                  <!-- 椭圆节点 -->
                  <template v-else-if="node.shape === 'ellipse'">
                    <ellipse :cx="node.x" :cy="node.y" :rx="node.width/2 + 5" :ry="node.height/2 + 5"
                             fill="transparent" class="drag-area"/>
                    <rect v-if="selectedElement && selectedElement.id === node.id"
                          :x="node.x - node.width/2 - 5" :y="node.y - node.height/2 - 5"
                          :width="node.width + 10" :height="node.height + 10"
                          fill="none" stroke="#1890ff" stroke-width="2" stroke-dasharray="5,5"/>
                    <ellipse :cx="node.x" :cy="node.y" :rx="node.width/2" :ry="node.height/2"
                             fill="white" :stroke="lineColor" :stroke-width="lineWidth"/>
                  </template>
                  <!-- 文字 -->
                  <g v-if="!(editingElement && editingElement.id === node.id)">
                    <text :x="node.x" :y="node.y + 4" text-anchor="middle" class="node-label"
                          :style="dynamicFontStyle" @click.stop="startEdit(node)">{{ node.name }}</text>
                  </g>
                  <foreignObject v-if="editingElement && editingElement.id === node.id"
                                 :x="node.x - 70" :y="node.y - 15" width="140" height="30">
                    <input :ref="'editInput_' + node.id" v-model="editingText"
                           @keydown.enter="saveEdit" @keydown.esc="cancelEdit"
                           @mousedown.stop @click.stop class="inline-edit-input" type="text"/>
                  </foreignObject>
                  <!-- 删除按钮 -->
                  <circle v-if="selectedElement && selectedElement.id === node.id"
                          :cx="node.x + node.width/2 + 10" :cy="node.y - node.height/2 - 10"
                          r="8" fill="#ff4d4f" stroke="white" stroke-width="2" class="delete-btn"
                          @click.stop="deleteElement(node)"/>
                  <text v-if="selectedElement && selectedElement.id === node.id"
                        :x="node.x + node.width/2 + 10" :y="node.y - node.height/2 - 6"
                        text-anchor="middle" fill="white" font-size="12" class="delete-btn"
                        @click.stop="deleteElement(node)">×</text>
                  <!-- 缩放控制点 -->
                  <g v-if="selectedElement && selectedElement.id === node.id" class="resize-handles">
                    <circle :cx="node.x + node.width/2" :cy="node.y" r="4"
                            fill="#1890ff" stroke="white" stroke-width="2" class="resize-handle resize-width"
                            @mousedown.stop="onResizeStart(node, $event, 'width')"/>
                    <circle :cx="node.x" :cy="node.y + node.height/2" r="4"
                            fill="#1890ff" stroke="white" stroke-width="2" class="resize-handle resize-height"
                            @mousedown.stop="onResizeStart(node, $event, 'height')"/>
                    <circle :cx="node.x + node.width/2" :cy="node.y + node.height/2" r="4"
                            fill="#52c41a" stroke="white" stroke-width="2" class="resize-handle resize-both"
                            @mousedown.stop="onResizeStart(node, $event, 'both')"/>
                  </g>
                </g>
              </g>
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- 历史流程图对话框 -->
    <el-dialog
      title="历史流程图"
      :visible.sync="showHistoryModal"
      width="1000px"
      :before-close="closeHistoryDialog">
      <div class="history-content">
        <!-- 说明 -->
        <div class="history-description" style="margin-bottom: 12px;">
          <ul>
            <li>• 共有 {{ historyList.length }} 个已保存的流程图（按保存时间倒序排列）</li>
            <li>• 点击"加载"按钮可以加载对应的流程图</li>
            <li>• 系统最多保存20条历史流程图记录</li>
          </ul>
        </div>

        <el-table
          :data="paginatedHistoryList"
          stripe
          style="width: 100%"
          empty-text="暂无保存的历史流程图">
          <el-table-column type="index" label="序号" width="60" align="center">
            <template slot-scope="scope">
              {{ (currentPage - 1) * pageSize + scope.$index + 1 }}
            </template>
          </el-table-column>

          <el-table-column label="课题名称" min-width="150" align="center">
            <template slot-scope="scope">
              <span class="diagram-name" :class="{ current: currentDiagramId === scope.row.id }">
                {{ scope.row.projectName || '-' }}
              </span>
            </template>
          </el-table-column>

          <el-table-column label="流程描述" width="120" align="center">
            <template slot-scope="scope">
              <el-button
                v-if="scope.row.flowDescription"
                size="mini"
                type="text"
                @click="viewFlowDescription(scope.row.flowDescription)">
                查看
              </el-button>
              <span v-else style="color:#ccc;">-</span>
            </template>
          </el-table-column>

          <el-table-column label="流程图内容" width="200" align="center">
            <template slot-scope="scope">
              <div class="diagram-content">{{ getFlowchartStats(scope.row) }}</div>
            </template>
          </el-table-column>

          <el-table-column label="保存时间" width="180" align="center">
            <template slot-scope="scope">
              <div class="absolute-time">{{ scope.row.createdAt }}</div>
            </template>
          </el-table-column>

          <el-table-column label="操作" width="180" align="center" fixed="right">
            <template slot-scope="scope">
              <div class="action-buttons">
                <el-button size="mini" type="primary" @click="loadHistory(scope.row)">加载</el-button>
                <el-button size="mini" type="danger" @click="deleteHistory(scope.row)">删除</el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination
            :current-page.sync="currentPage"
            :page-size="pageSize"
            :total="historyList.length"
            @current-change="handlePageChange"
            layout="prev, pager, next" />
        </div>
      </div>
    </el-dialog>

    <!-- 导出对话框 -->
    <ExportDialog :visible.sync="isExportDialogVisible" @confirm="handleExportConfirm" />

    <!-- 流程描述查看弹框 -->
    <el-dialog title="流程描述" :visible.sync="showDescDialog" width="500px">
      <div style="white-space: pre-wrap; line-height: 1.8; font-size: 14px; color: #333; height: 300px; overflow-y: auto; padding: 4px 8px;">{{ currentDesc }}</div>
      <span slot="footer">
        <el-button @click="showDescDialog = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import ExportDialog from '@/components/ExportDialog.vue'

export default {
  name: 'Flowchart',
  components: { ExportDialog },
  data() {
    return {
      aiForm: { projectName: '用户登录流程', flowDescription: `1. 用户打开登录页面
2. 输入账号和密码
3. 系统验证账号密码
4. 验证成功则跳转首页
5. 验证失败则提示错误并重试` },
      // 画布元素
      nodes: [
        { id: 'node_1', name: '开始',           x: 400, y: 60,  width: 120, height: 44, shape: 'terminator', type: 'terminator' },
        { id: 'node_2', name: '登录页面',        x: 400, y: 150, width: 140, height: 40, shape: 'rect',       type: 'rect' },
        { id: 'node_3', name: '输入用户名密码',  x: 400, y: 240, width: 200, height: 40, shape: 'parallelogram', type: 'parallelogram' },
        { id: 'node_4', name: '验证',            x: 400, y: 340, width: 140, height: 60, shape: 'diamond',    type: 'diamond' },
        { id: 'node_5', name: '登录成功进入系统',x: 400, y: 460, width: 200, height: 44, shape: 'rect',       type: 'rect' },
        { id: 'node_6', name: '结束',            x: 400, y: 560, width: 120, height: 44, shape: 'terminator', type: 'terminator' }
      ],
      boundaries: [],
      relationships: [
        { id: 'rel_1', sourceId: 'node_1', targetId: 'node_2', sourceSide: 'bottom', targetSide: 'top',    x1: 400, y1: 82,  x2: 400, y2: 130, label: '',  type: 'arrow-line', hasArrow: true, isDashed: false, isHollow: false },
        { id: 'rel_2', sourceId: 'node_2', targetId: 'node_3', sourceSide: 'bottom', targetSide: 'top',    x1: 400, y1: 170, x2: 400, y2: 220, label: '',  type: 'arrow-line', hasArrow: true, isDashed: false, isHollow: false },
        { id: 'rel_3', sourceId: 'node_3', targetId: 'node_4', sourceSide: 'bottom', targetSide: 'top',    x1: 400, y1: 260, x2: 400, y2: 310, label: '',  type: 'arrow-line', hasArrow: true, isDashed: false, isHollow: false },
        { id: 'rel_4', sourceId: 'node_4', targetId: 'node_5', sourceSide: 'bottom', targetSide: 'top',    x1: 400, y1: 370, x2: 400, y2: 438, label: '是', type: 'arrow-line', hasArrow: true, isDashed: false, isHollow: false },
        { id: 'rel_5', sourceId: 'node_5', targetId: 'node_6', sourceSide: 'bottom', targetSide: 'top',    x1: 400, y1: 482, x2: 400, y2: 538, label: '',  type: 'arrow-line', hasArrow: true, isDashed: false, isHollow: false },
        { id: 'rel_6', sourceId: 'node_4', targetId: null,     sourceSide: 'right',  targetSide: null,     x1: 470, y1: 340, x2: 560, y2: 340, label: '否', type: 'arrow-line', hasArrow: false, isDashed: false, isHollow: false },
        { id: 'rel_7', sourceId: null,     targetId: null,     sourceSide: null,     targetSide: null,     x1: 560, y1: 340, x2: 560, y2: 240, label: '',  type: 'arrow-line', hasArrow: false, isDashed: false, isHollow: false },
        { id: 'rel_8', sourceId: null,     targetId: 'node_3', sourceSide: null,     targetSide: 'right',  x1: 560, y1: 240, x2: 500, y2: 240, label: '',  type: 'arrow-line', hasArrow: true,  isDashed: false, isHollow: false }
      ],
      nextId: 1,
      // 选中 / 编辑
      selectedElement: null,
      editingElement: null,
      editingText: '',
      // 拖拽
      isDragging: false,
      dragOffset: { x: 0, y: 0 },
      // 平移
      isPanning: false,
      panStartX: 0,
      panStartY: 0,
      panX: 0,
      panY: 0,
      canvasVirtualWidth: 1300,
      canvasVirtualHeight: 1000,
      zoomLevel: 1,
      // 全屏
      isFullscreen: false,
      // 悬停线
      hoveredLine: null,
      // 文字拖拽
      isDraggingText: false,
      textDragOffset: { x: 0, y: 0 },
      preventTextEdit: false,
      // 缩放
      isResizing: false,
      resizeType: null,
      resizeStartPos: { x: 0, y: 0 },
      originalSize: { width: 0, height: 0 },
      // 端点拖拽
      isDraggingEndpoint: false,
      draggingEndpoint: null,
      draggingLine: null,
      // 线条拖拽
      isDraggingLine: false,
      // 参数
      fontFamily: 'SimSun',
      fontSize: 22,
      fontWeight: 'normal',
      fontColor: '#333333',
      lineColor: '#333333',
      lineWidth: 1,
      arrowSize: 'medium',
      // 历史
      showHistoryModal: false,
      historyList: [],
      currentDiagramId: null,
      currentPage: 1,
      pageSize: 10,
      // 描述查看
      showDescDialog: false,
      currentDesc: '',
      // 导出
      isExportDialogVisible: false,
      // 积分配置
      pointsConfig: {
        flowchart: 3
      }
    }
  },
  computed: {
    computedViewBox() {
      const w = this.canvasVirtualWidth / this.zoomLevel
      const h = this.canvasVirtualHeight / this.zoomLevel
      return `${-this.panX} ${-this.panY} ${w} ${h}`
    },
    dynamicFontStyle() {
      return { fontFamily: this.fontFamily, fontSize: this.fontSize + 'px', fill: this.fontColor, fontWeight: this.fontWeight }
    },
    arrowDimensions() {
      const s = { small: { width:8, height:6, refX:7, refY:3, points:'0 0, 8 3, 0 6' }, medium: { width:10, height:7, refX:9, refY:3.5, points:'0 0, 10 3.5, 0 7' }, large: { width:12, height:8, refX:11, refY:4, points:'0 0, 12 4, 0 8' } }
      return s[this.arrowSize] || s.medium
    },
    hollowArrowDimensions() {
      const s = { small: { width:10, height:7, refX:9, refY:3.5, points:'0 0, 10 3.5, 0 7' }, medium: { width:12, height:8, refX:11, refY:4, points:'0 0, 12 4, 0 8' }, large: { width:14, height:9, refX:13, refY:4.5, points:'0 0, 14 4.5, 0 9' } }
      return s[this.arrowSize] || s.medium
    },
    canSave() {
      return this.nodes.length > 0 || this.boundaries.length > 0 || this.relationships.length > 0
    },
    paginatedHistoryList() {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.historyList.slice(start, end)
    }
  },
  mounted() {
    this.loadHistoryList();
    this.increasePageView();
    this.loadPointsConfig();
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);

    // 默认加载第一个历史流程图
    this.$nextTick(() => {
      if (this.historyList.length > 0) {
        const firstDiagram = this.historyList[0];
        this.loadHistoryDiagram(firstDiagram);
      }
    });
  },
  beforeDestroy() {
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
  },
  methods: {
    // ── 保存 / 历史 ──────────────────────────────────────────────────────────
    saveCurrentDiagram() {
      if (!this.canSave) { this.$message.warning('当前流程图为空，无法保存'); return }
      if (!this.aiForm.projectName.trim()) { this.$message.warning('请输入课题名称'); return }
      const data = {
        id: this.currentDiagramId || 'flowchart_' + Date.now(),
        projectName: this.aiForm.projectName,
        flowDescription: this.aiForm.flowDescription,
        nodes: JSON.parse(JSON.stringify(this.nodes)),
        boundaries: JSON.parse(JSON.stringify(this.boundaries)),
        relationships: JSON.parse(JSON.stringify(this.relationships)),
        settings: { fontFamily: this.fontFamily, fontSize: this.fontSize, fontWeight: this.fontWeight, fontColor: this.fontColor, lineColor: this.lineColor, lineWidth: this.lineWidth, arrowSize: this.arrowSize },
        createdAt: new Date().toLocaleString()
      }
      const list = this.loadHistoryList()
      const idx = list.findIndex(i => i.id === data.id)
      if (idx > -1) list[idx] = data; else list.unshift(data)
      localStorage.setItem('flowchart_history', JSON.stringify(list))
      this.currentDiagramId = data.id
      this.$message.success('保存成功')
    },
    loadHistoryList() {
      try { const list = JSON.parse(localStorage.getItem('flowchart_history') || '[]'); this.historyList = list; return list } catch { return [] }
    },
    showHistoryDialog() {
      this.loadHistoryList()
      this.currentPage = 1
      this.showHistoryModal = true
    },
    closeHistoryDialog() {
      this.showHistoryModal = false
    },
    loadHistory(row) {
      this.$confirm('加载流程图将覆盖当前内容，是否继续？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.loadHistoryDiagram(row);
        this.showHistoryModal = false;
        this.$message.success('加载成功');
      }).catch(() => {})
    },
    loadHistoryDiagram(row) {
      this.aiForm.projectName = row.projectName || '';
      this.aiForm.flowDescription = row.flowDescription || '';
      this.nodes = row.nodes || [];
      this.boundaries = row.boundaries || [];
      this.relationships = row.relationships || [];
      if (row.settings) { Object.assign(this, row.settings); }
      this.currentDiagramId = row.id;
    },
    deleteHistory(row) {
      this.$confirm('确定删除这条历史记录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const list = this.historyList.filter(i => i.id !== row.id)
        localStorage.setItem('flowchart_history', JSON.stringify(list))
        this.historyList = list
        this.$message.success('删除成功')
      }).catch(() => {})
    },
    handlePageChange(page) {
      this.currentPage = page
    },
    getFlowchartStats(diagram) {
      const nodeCount = (diagram.nodes || []).length
      const boundaryCount = (diagram.boundaries || []).length
      const relationCount = (diagram.relationships || []).length

      const parts = []
      if (nodeCount > 0) parts.push(nodeCount + '个节点')
      if (boundaryCount > 0) parts.push(boundaryCount + '个边界')
      if (relationCount > 0) parts.push(relationCount + '个关系')

      return parts.length > 0 ? parts.join(', ') : '空流程图'
    },
    viewFlowDescription(desc) {
      this.currentDesc = desc
      this.showDescDialog = true
    },
    // ── AI 生成 ──────────────────────────────────────────────────────────────
    submitAIGenerate() {
      if (!this.aiForm.projectName.trim()) { this.$message.warning('请输入课题名称'); return }
      if (!this.aiForm.flowDescription.trim()) { this.$message.warning('请输入流程描述'); return }

      // 添加积分确认
      this.$confirm(`此操作将消耗${this.pointsConfig.flowchart}积分，是否继续？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.doAIGenerate()
      }).catch(() => {
        this.$message.info('已取消操作')
      })
    },

    // 执行AI生成
    doAIGenerate() {
      const loading = this.$loading({ lock: true, text: 'AI正在生成流程图，请稍候...', spinner: 'el-icon-loading', background: 'rgba(0,0,0,0.7)' })

      this.request.post('/ai/generateFlowchart', {
        projectName: this.aiForm.projectName,
        flowDescription: this.aiForm.flowDescription
      }).then(res => {
        loading.close()
        if (res.code === '200') {
          const data = res.data
          this.nodes = data.nodes || []
          this.boundaries = data.boundaries || []
          this.relationships = data.relationships || []
          this.selectedElement = null
          this.hoveredLine = null
          this.panX = 0; this.panY = 0; this.zoomLevel = 1
          this.$message.success('流程图生成成功！')

          // 刷新积分
          this.refreshPoints()

          // 自动保存生成的流程图
          this.$nextTick(() => {
            this.autoSaveDiagram()
          })
        } else {
          this.$message.error(res.msg || '生成失败')
        }
      }).catch(err => {
        loading.close()
        this.$message.error('生成失败：' + (err.message || '网络错误'))
      })
    },

    // 自动保存流程图
    autoSaveDiagram() {
      if (!this.canSave) return
      if (!this.aiForm.projectName.trim()) return

      const data = {
        id: this.currentDiagramId || 'flowchart_' + Date.now(),
        projectName: this.aiForm.projectName,
        flowDescription: this.aiForm.flowDescription,
        nodes: JSON.parse(JSON.stringify(this.nodes)),
        boundaries: JSON.parse(JSON.stringify(this.boundaries)),
        relationships: JSON.parse(JSON.stringify(this.relationships)),
        settings: { fontFamily: this.fontFamily, fontSize: this.fontSize, fontWeight: this.fontWeight, fontColor: this.fontColor, lineColor: this.lineColor, lineWidth: this.lineWidth, arrowSize: this.arrowSize },
        createdAt: new Date().toLocaleString()
      }
      const list = this.loadHistoryList()
      const idx = list.findIndex(i => i.id === data.id)
      if (idx > -1) list[idx] = data; else list.unshift(data)
      localStorage.setItem('flowchart_history', JSON.stringify(list))
      this.currentDiagramId = data.id
    },
    // ── 画布事件 ─────────────────────────────────────────────────────────────
    onCanvasClick(event) {
      if (event.target === event.currentTarget) this.clearSelection()
    },
    onCanvasMouseDown(event) {
      if (event.target === this.$refs.diagramSvg) {
        if (this.editingElement) this.saveEdit()
        this.clearSelection()
        this.isPanning = true
        this.panStartX = event.clientX
        this.panStartY = event.clientY
        event.preventDefault()
      }
    },
    onCanvasMouseMove(event) {
      if (this.isPanning) {
        const rect = this.$refs.diagramSvg.getBoundingClientRect()
        const sx = (this.canvasVirtualWidth / this.zoomLevel) / rect.width
        const sy = (this.canvasVirtualHeight / this.zoomLevel) / rect.height
        this.panX += (event.clientX - this.panStartX) * sx
        this.panY += (event.clientY - this.panStartY) * sy
        this.panStartX = event.clientX
        this.panStartY = event.clientY
        return
      }
      const rect = this.$refs.diagramSvg.getBoundingClientRect()
      const sx = (this.canvasVirtualWidth / this.zoomLevel) / rect.width
      const sy = (this.canvasVirtualHeight / this.zoomLevel) / rect.height
      const cx = (event.clientX - rect.left) * sx - this.panX
      const cy = (event.clientY - rect.top) * sy - this.panY
      if (this.isDraggingEndpoint && this.draggingLine) {
        if (this.draggingEndpoint === 'start') { this.draggingLine.x1 = cx; this.draggingLine.y1 = cy }
        else { this.draggingLine.x2 = cx; this.draggingLine.y2 = cy }
      }
      if (this.isDraggingLine && this.selectedElement) {
        const dx = cx - this.dragOffset.x; const dy = cy - this.dragOffset.y
        this.selectedElement.x1 += dx; this.selectedElement.x2 += dx
        this.selectedElement.y1 += dy; this.selectedElement.y2 += dy
        this.dragOffset.x = cx; this.dragOffset.y = cy
      }
      if (this.isDraggingText && this.selectedElement) {
        this.selectedElement.textX = cx - this.textDragOffset.x
        this.selectedElement.textY = cy - this.textDragOffset.y
      }
      if (this.isDragging && this.selectedElement && !this.isResizing && !this.isDraggingEndpoint && !this.isDraggingLine && !this.isDraggingText) {
        const oldX = this.selectedElement.x; const oldY = this.selectedElement.y
        this.selectedElement.x = cx - this.dragOffset.x
        this.selectedElement.y = cy - this.dragOffset.y
        if (this.selectedElement.textX !== undefined) {
          this.selectedElement.textX += this.selectedElement.x - oldX
          this.selectedElement.textY += this.selectedElement.y - oldY
        }
        this.updateRelationships()
      }
      if (this.isResizing && this.selectedElement) {
        const dx = cx - this.resizeStartPos.x; const dy = cy - this.resizeStartPos.y
        if (this.resizeType === 'width' || this.resizeType === 'both') this.selectedElement.width = Math.max(60, this.originalSize.width + dx * 2)
        if (this.resizeType === 'height' || this.resizeType === 'both') this.selectedElement.height = Math.max(30, this.originalSize.height + dy * 2)
      }
    },
    onCanvasMouseUp() {
      this.isDragging = false; this.isResizing = false; this.resizeType = null
      this.isDraggingEndpoint = false; this.draggingEndpoint = null; this.draggingLine = null
      this.isDraggingLine = false; this.isDraggingText = false; this.isPanning = false
    },
    // ── 元素操作 ─────────────────────────────────────────────────────────────
    onElementMouseDown(element, event) {
      this.selectedElement = element; this.isDragging = true
      if (this.editingElement && this.editingElement.id !== element.id) this.saveEdit()
      const rect = this.$refs.diagramSvg.getBoundingClientRect()
      const sx = (this.canvasVirtualWidth / this.zoomLevel) / rect.width
      const sy = (this.canvasVirtualHeight / this.zoomLevel) / rect.height
      this.dragOffset = { x: (event.clientX - rect.left) * sx - this.panX - element.x, y: (event.clientY - rect.top) * sy - this.panY - element.y }
      event.stopPropagation()
    },
    startLineDrag(line, event) {
      this.isDraggingLine = true; this.selectedElement = line
      const rect = this.$refs.diagramSvg.getBoundingClientRect()
      const sx = (this.canvasVirtualWidth / this.zoomLevel) / rect.width
      const sy = (this.canvasVirtualHeight / this.zoomLevel) / rect.height
      this.dragOffset = { x: (event.clientX - rect.left) * sx - this.panX, y: (event.clientY - rect.top) * sy - this.panY }
      event.stopPropagation()
    },
    startEndpointDrag(line, endpoint, event) {
      this.isDraggingEndpoint = true; this.draggingEndpoint = endpoint; this.draggingLine = line; this.selectedElement = line
      event.stopPropagation()
    },
    startTextDrag(boundary, event) {
      this.selectedElement = boundary; this.isDraggingText = true
      const rect = this.$refs.diagramSvg.getBoundingClientRect()
      const sx = (this.canvasVirtualWidth / this.zoomLevel) / rect.width
      const sy = (this.canvasVirtualHeight / this.zoomLevel) / rect.height
      const svgX = (event.clientX - rect.left) * sx - this.panX
      const svgY = (event.clientY - rect.top) * sy - this.panY
      this.textDragOffset = { x: svgX - boundary.textX, y: svgY - boundary.textY }
      this.preventTextEdit = true
      const vm = this; setTimeout(() => { vm.preventTextEdit = false }, 200)
      event.stopPropagation()
    },
    onResizeStart(element, event, type) {
      this.isResizing = true; this.resizeType = type || 'both'; this.selectedElement = element
      const rect = this.$refs.diagramSvg.getBoundingClientRect()
      const sx = (this.canvasVirtualWidth / this.zoomLevel) / rect.width
      const sy = (this.canvasVirtualHeight / this.zoomLevel) / rect.height
      this.resizeStartPos = { x: (event.clientX - rect.left) * sx - this.panX, y: (event.clientY - rect.top) * sy - this.panY }
      this.originalSize = { width: element.width, height: element.height }
      event.stopPropagation()
    },
    startEdit(element) {
      if (this.preventTextEdit) return
      this.editingElement = element; this.editingText = element.name || element.label || ''; this.selectedElement = element
      const vm = this; setTimeout(() => { const r = vm.$refs['editInput_' + element.id]; if (r && r[0]) { r[0].focus(); r[0].select() } }, 50)
    },
    saveEdit() {
      if (this.editingElement) {
        if (this.editingElement.name !== undefined) { if (this.editingText && this.editingText.trim()) this.editingElement.name = this.editingText.trim() }
        else if (this.editingElement.label !== undefined) this.editingElement.label = this.editingText ? this.editingText.trim() : ''
      }
      this.editingElement = null; this.editingText = ''
    },
    cancelEdit() { this.editingElement = null; this.editingText = '' },
    deleteElement(element) {
      if (element.type === 'boundary' || element.type === 'solid-boundary') {
        const idx = this.boundaries.findIndex(b => b.id === element.id); if (idx > -1) this.boundaries.splice(idx, 1)
      } else if (['arrow-line','straight-line','dashed-arrow-line','dashed-straight-line','generalization-line'].includes(element.type)) {
        const idx = this.relationships.findIndex(r => r.id === element.id); if (idx > -1) this.relationships.splice(idx, 1)
      } else {
        const idx = this.nodes.findIndex(n => n.id === element.id); if (idx > -1) this.nodes.splice(idx, 1)
      }
      this.selectedElement = null
    },
    deleteText(element) { element.label = '' },
    selectLine(line) { this.selectedElement = line; if (this.editingElement && this.editingElement.id !== line.id) this.saveEdit() },
    setHoveredLine(line) { this.hoveredLine = line },
    clearHoveredLine() { this.hoveredLine = null },
    clearSelection() { if (this.editingElement) this.saveEdit(); this.selectedElement = null; this.hoveredLine = null },
    // ── 连线跟随节点移动 ─────────────────────────────────────────────────────
    updateRelationships() {
      this.relationships.forEach(rel => {
        const fromId   = rel.fromId !== undefined ? rel.fromId : rel.sourceId
        const toId     = rel.toId   !== undefined ? rel.toId   : rel.targetId
        const fromSide = rel.fromSide || rel.sourceSide || 'bottom'
        const toSide   = rel.toSide   || rel.targetSide || 'top'

        const src = fromId !== undefined && fromId !== -1
          ? this.nodes.find(n => String(n.id) === String(fromId)) : null
        const tgt = toId !== undefined && toId !== -1
          ? this.nodes.find(n => String(n.id) === String(toId)) : null

        if (src) { const pt = this.getNodeConnectionPoint(src, fromSide); rel.x1 = pt.x; rel.y1 = pt.y }
        if (tgt) { const pt = this.getNodeConnectionPoint(tgt, toSide);   rel.x2 = pt.x; rel.y2 = pt.y }

        // 折线 waypoints 联动：保持右侧 x 不变，更新 y
        if (rel.waypoints && rel.waypoints.length === 2) {
          const rightX = rel.waypoints[0].x  // 右侧固定 x
          rel.waypoints[0].x = rightX; rel.waypoints[0].y = rel.y1
          rel.waypoints[1].x = rightX; rel.waypoints[1].y = rel.y2
        }
      })

      // 默认示例折线（rel_6/7/8）
      const n4 = this.nodes.find(n => n.id === 'node_4')
      const n3 = this.nodes.find(n => n.id === 'node_3')
      const rel6 = this.relationships.find(r => r.id === 'rel_6')
      const rel7 = this.relationships.find(r => r.id === 'rel_7')
      const rel8 = this.relationships.find(r => r.id === 'rel_8')
      if (n4 && n3 && rel6 && rel7 && rel8) {
        const rightX = Math.max(n4.x, n3.x) + Math.max(n4.width, n3.width) / 2 + 90
        rel6.x1 = n4.x + n4.width / 2; rel6.y1 = n4.y; rel6.x2 = rightX; rel6.y2 = n4.y
        rel7.x1 = rightX; rel7.y1 = n4.y; rel7.x2 = rightX; rel7.y2 = n3.y
        rel8.x1 = rightX; rel8.y1 = n3.y; rel8.x2 = n3.x + n3.width / 2; rel8.y2 = n3.y
      }
    },
    getNodeConnectionPoint(node, side) {
      switch (side) {
        case 'top':    return { x: node.x, y: node.y - node.height / 2 }
        case 'bottom': return { x: node.x, y: node.y + node.height / 2 }
        case 'left':   return { x: node.x - node.width / 2, y: node.y }
        case 'right':  return { x: node.x + node.width / 2, y: node.y }
        default:       return { x: node.x, y: node.y }
      }
    },

    // 折线所有点拼成 SVG polyline points 字符串
    getPolylinePoints(rel) {
      const pts = [[rel.x1, rel.y1], ...(rel.waypoints || []).map(p => [p.x, p.y]), [rel.x2, rel.y2]]
      return pts.map(p => `${p[0]},${p[1]}`).join(' ')
    },

    // 折线生成 SVG path d 属性（支持 marker-end）
    getPolylinePath(rel) {
      const pts = [[rel.x1, rel.y1], ...(rel.waypoints || []).map(p => [p.x, p.y]), [rel.x2, rel.y2]]
      return pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0]},${p[1]}`).join(' ')
    },

    // 标签 x：折线取第一段中点，直线取中点，竖线左移
    getLabelX(rel) {
      if (rel.waypoints && rel.waypoints.length) {
        return (rel.x1 + rel.waypoints[0].x) / 2 + 8
      }
      return rel.x1 === rel.x2 ? rel.x1 - 16 : (rel.x1 + rel.x2) / 2
    },

    // 标签 y：折线取第一段中点，直线取中点上移
    getLabelY(rel) {
      if (rel.waypoints && rel.waypoints.length) {
        return (rel.y1 + rel.waypoints[0].y) / 2 - 8
      }
      return rel.y1 === rel.y2 ? rel.y1 - 8 : (rel.y1 + rel.y2) / 2 - 8
    },
    // ── 缩放 / 全屏 / 清空 ───────────────────────────────────────────────────
    zoomIn() { this.zoomLevel = Math.min(3, parseFloat((this.zoomLevel + 0.2).toFixed(1))) },
    zoomOut() { this.zoomLevel = Math.max(0.2, parseFloat((this.zoomLevel - 0.2).toFixed(1))) },
    clearCanvas() {
      this.$confirm('确定清空画布？', '提示', { type: 'warning' }).then(() => {
        this.nodes = []; this.boundaries = []; this.relationships = []
        this.selectedElement = null; this.hoveredLine = null; this.editingElement = null; this.editingText = ''
      }).catch(() => {})
    },
    toggleFullscreen() {
      const el = document.querySelector('.flowchart-editor')
      if (!this.isFullscreen) { if (el.requestFullscreen) el.requestFullscreen(); this.isFullscreen = true }
      else { if (document.exitFullscreen) document.exitFullscreen(); this.isFullscreen = false }
    },
    handleFullscreenChange() { this.isFullscreen = !!document.fullscreenElement },
    // ── 参数 ─────────────────────────────────────────────────────────────────
    updateFont() {}, updateLineStyle() {}, updateArrowSize() {},
    increaseLineWidth() { if (this.lineWidth < 5) this.lineWidth++ },
    decreaseLineWidth() { if (this.lineWidth > 1) this.lineWidth-- },
    getMarkerEnd(rel) {
      if (!rel.hasArrow || this.arrowSize === 'none') return 'none'
      return rel.isHollow ? 'url(#hollow-arrowhead)' : 'url(#arrowhead)'
    },
    // ── 创建元素 ─────────────────────────────────────────────────────────────
    parallelogramPoints(node) {
      const skew = 16
      const x = node.x; const y = node.y; const w = node.width / 2; const h = node.height / 2
      return `${x - w + skew},${y - h} ${x + w + skew},${y - h} ${x + w - skew},${y + h} ${x - w - skew},${y + h}`
    },
    createElement(type, name, x, y) {
      const id = type + '_' + this.nextId++
      if (type === 'rect') this.nodes.push({ id, name, x, y, width: 120, height: 50, shape: 'rect', type })
      else if (type === 'diamond') this.nodes.push({ id, name, x, y, width: 120, height: 60, shape: 'diamond', type })
      else if (type === 'ellipse') this.nodes.push({ id, name, x, y, width: 120, height: 50, shape: 'ellipse', type })
      else if (type === 'boundary') this.boundaries.push({ id, name, x, y, width: 200, height: 150, type, isDashed: true, textX: x, textY: y - 60 })
      else if (type === 'solid-boundary') this.boundaries.push({ id, name, x, y, width: 200, height: 150, type, isDashed: false, textX: x, textY: y - 60 })
      else if (['arrow-line','straight-line','dashed-arrow-line','dashed-straight-line','generalization-line'].includes(type)) {
        const hasArrow = type !== 'straight-line' && type !== 'dashed-straight-line'
        const isDashed = type.startsWith('dashed')
        const isHollow = type === 'generalization-line'
        this.relationships.push({ id, x1: x - 80, y1: y, x2: x + 80, y2: y, label: name, type, hasArrow, isDashed, isHollow })
      }
    },
    // ── 导出 ─────────────────────────────────────────────────────────────────
    openExportDialog() { this.isExportDialogVisible = true },
    handleExportConfirm(options) {
      if (options.format === 'drawio') {
        this.exportToDrawio()
      } else {
        this.exportToImage(options)
      }
    },

    exportToDrawio() {
      try {
        const cells = []
        let id = 2
        const nodeIdMap = {}

        // 节点形状 → draw.io style
        const shapeStyle = {
          rect:          () => `rounded=0;whiteSpace=wrap;html=1;fontSize=${this.fontSize};fontFamily=${this.fontFamily};`,
          terminator:    () => `strokeWidth=1;html=1;shape=mxgraph.flowchart.terminator;whiteSpace=wrap;fontSize=${this.fontSize};fontFamily=${this.fontFamily};`,
          diamond:       () => `rhombus;whiteSpace=wrap;html=1;fontSize=${this.fontSize};fontFamily=${this.fontFamily};`,
          parallelogram: () => `shape=parallelogram;perimeter=parallelogramPerimeter;whiteSpace=wrap;html=1;fixedSize=1;fontSize=${this.fontSize};fontFamily=${this.fontFamily};`,
          ellipse:       () => `ellipse;whiteSpace=wrap;html=1;fontSize=${this.fontSize};fontFamily=${this.fontFamily};`
        }

        // 导出节点
        this.nodes.forEach(node => {
          nodeIdMap[String(node.id)] = id
          const style = (shapeStyle[node.shape] || shapeStyle.rect)()
          const x = Math.round(node.x - node.width / 2)
          const y = Math.round(node.y - node.height / 2)
          cells.push(
            `<mxCell id="${id}" value="${this.escapeXml(node.name)}" vertex="1" parent="1" style="${style}">` +
            `<mxGeometry x="${x}" y="${y}" width="${node.width}" height="${node.height}" as="geometry"/>` +
            `</mxCell>`
          )
          id++
        })

        // 导出边界框
        this.boundaries.forEach(b => {
          nodeIdMap[String(b.id)] = id
          const style = b.isDashed
            ? `rounded=0;whiteSpace=wrap;html=1;dashed=1;fontSize=${this.fontSize};fontFamily=${this.fontFamily};`
            : `rounded=0;whiteSpace=wrap;html=1;fontSize=${this.fontSize};fontFamily=${this.fontFamily};`
          cells.push(
            `<mxCell id="${id}" value="${this.escapeXml(b.name)}" vertex="1" parent="1" style="${style}">` +
            `<mxGeometry x="${Math.round(b.x - b.width/2)}" y="${Math.round(b.y - b.height/2)}" width="${b.width}" height="${b.height}" as="geometry"/>` +
            `</mxCell>`
          )
          id++
        })

        // 导出连线
        this.relationships.forEach(rel => {
          // 兼容 fromId/toId 和 sourceId/targetId 两种字段名
          const fromId = rel.fromId !== undefined ? rel.fromId : rel.sourceId
          const toId   = rel.toId   !== undefined ? rel.toId   : rel.targetId

          const srcCellId = fromId !== undefined && fromId !== null ? nodeIdMap[String(fromId)] : null
          const tgtCellId = toId   !== undefined && toId   !== null ? nodeIdMap[String(toId)]   : null

          const calcRatio = (nodeId, px, py) => {
            const node = this.nodes.find(n => String(n.id) === String(nodeId))
            if (!node) return null
            return {
              x: Math.max(0, Math.min(1, (px - (node.x - node.width/2))  / node.width)),
              y: Math.max(0, Math.min(1, (py - (node.y - node.height/2)) / node.height))
            }
          }

          let exitAttr = '', entryAttr = ''
          if (srcCellId && fromId !== undefined && fromId !== null) {
            const r = calcRatio(fromId, rel.x1, rel.y1)
            if (r) exitAttr = `;exitX=${r.x.toFixed(3)};exitY=${r.y.toFixed(3)};exitDx=0;exitDy=0`
          }
          if (tgtCellId && toId !== undefined && toId !== null) {
            const r = calcRatio(toId, rel.x2, rel.y2)
            if (r) entryAttr = `;entryX=${r.x.toFixed(3)};entryY=${r.y.toFixed(3)};entryDx=0;entryDy=0`
          }

          let style = `rounded=0;html=1;fontSize=${this.fontSize};fontFamily=${this.fontFamily};`
          style += rel.hasArrow ? 'endArrow=classic;' : 'endArrow=none;endFill=0;'
          if (rel.isDashed) style += 'dashed=1;'
          if (rel.label) style += 'labelBackgroundColor=#ffffff;'
          style += exitAttr + entryAttr

          const srcAttr = srcCellId ? ` source="${srcCellId}"` : ''
          const tgtAttr = tgtCellId ? ` target="${tgtCellId}"` : ''
          // 折线：有 waypoints 用 Array as="points"
          let geom
          if (rel.waypoints && rel.waypoints.length) {
            const pts = rel.waypoints.map(p => `<mxPoint x="${Math.round(p.x)}" y="${Math.round(p.y)}"/>`).join('')
            geom = `<mxGeometry relative="1" as="geometry"><Array as="points">${pts}</Array></mxGeometry>`
          } else if (srcCellId && tgtCellId) {
            geom = `<mxGeometry relative="1" as="geometry"/>`
          } else {
            geom = `<mxGeometry relative="1" as="geometry">` +
              `<mxPoint x="${Math.round(rel.x1)}" y="${Math.round(rel.y1)}" as="sourcePoint"/>` +
              `<mxPoint x="${Math.round(rel.x2)}" y="${Math.round(rel.y2)}" as="targetPoint"/>` +
              `</mxGeometry>`
          }

          const edgeId = id++
          cells.push(
            `<mxCell id="${edgeId}" value="" edge="1" parent="1"${srcAttr}${tgtAttr} style="${style}">` +
            geom + `</mxCell>`
          )

          // 标签
          if (rel.label && rel.label.trim()) {
            const lx = rel.waypoints && rel.waypoints.length
              ? Math.round((rel.x1 + rel.waypoints[0].x) / 2)
              : Math.round((rel.x1 + rel.x2) / 2)
            const ly = rel.waypoints && rel.waypoints.length
              ? Math.round((rel.y1 + rel.waypoints[0].y) / 2) - 10
              : Math.round((rel.y1 + rel.y2) / 2) - 10
            const escaped = rel.label.replace(/&/g,'&amp;').replace(/</g,'&amp;lt;').replace(/>/g,'&amp;gt;')
            const lv = `&lt;font style=&quot;font-size:${this.fontSize}px;&quot;&gt;${escaped}&lt;/font&gt;`
            cells.push(
              `<mxCell id="${id++}" value="${lv}" style="edgeLabel;html=1;align=center;verticalAlign=middle;resizable=0;points=[];fontFamily=${this.fontFamily};labelBackgroundColor=#ffffff;" connectable="0" vertex="1" parent="${edgeId}">` +
              `<mxGeometry relative="1" as="geometry"/>` +
              `</mxCell>`
            )
          }
        })

        const xml = `<?xml version="1.0" encoding="UTF-8"?>
<mxfile host="app.diagrams.net" modified="${new Date().toISOString()}" version="21.0.0" type="device">
  <diagram name="流程图" id="flowchart-diagram">
    <mxGraphModel dx="1422" dy="794" grid="1" gridSize="10" guides="1" tooltips="1" connect="1" arrows="1" fold="1" page="1" pageScale="1" pageWidth="827" pageHeight="1169" math="0" shadow="0">
      <root>
        <mxCell id="0"/>
        <mxCell id="1" parent="0"/>
        ${cells.join('\n        ')}
      </root>
    </mxGraphModel>
  </diagram>
</mxfile>`

        const blob = new Blob([xml], { type: 'application/xml;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        const ts = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '')
        a.download = `流程图_${ts}.drawio`
        a.href = url
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        this.$message.success('导出成功')
      } catch (e) {
        this.$message.error('导出失败：' + e.message)
      }
    },

    exportToImage(options) {
      const svg = this.$refs.diagramSvg
      if (!svg) return

      // 计算所有元素的实际边界
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
      this.nodes.forEach(n => {
        minX = Math.min(minX, n.x - n.width / 2)
        minY = Math.min(minY, n.y - n.height / 2)
        maxX = Math.max(maxX, n.x + n.width / 2)
        maxY = Math.max(maxY, n.y + n.height / 2)
      })
      this.relationships.forEach(r => {
        minX = Math.min(minX, r.x1, r.x2)
        minY = Math.min(minY, r.y1, r.y2)
        maxX = Math.max(maxX, r.x1, r.x2)
        maxY = Math.max(maxY, r.y1, r.y2)
      })
      if (!isFinite(minX)) { minX = 0; minY = 0; maxX = 800; maxY = 600 }

      const padding = 40
      const contentW = maxX - minX + padding * 2
      const contentH = maxY - minY + padding * 2

      // 克隆 SVG 并设置固定 viewBox 覆盖全部内容
      const clone = svg.cloneNode(true)
      clone.setAttribute('width', contentW)
      clone.setAttribute('height', contentH)
      clone.setAttribute('viewBox', `${minX - padding} ${minY - padding} ${contentW} ${contentH}`)

      const serializer = new XMLSerializer()
      const svgStr = serializer.serializeToString(clone)
      const svgBlob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' })
      const url = URL.createObjectURL(svgBlob)
      const img = new Image()
      img.onload = () => {
        const scale = 2
        const canvas = document.createElement('canvas')
        canvas.width = contentW * scale
        canvas.height = contentH * scale
        const ctx = canvas.getContext('2d')
        if (options.background === 'white') { ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height) }
        ctx.scale(scale, scale)
        ctx.drawImage(img, 0, 0, contentW, contentH)
        URL.revokeObjectURL(url)
        const mime = options.format === 'jpeg' ? 'image/jpeg' : 'image/png'
        const ts = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '')
        const a = document.createElement('a')
        a.download = `流程图_${ts}.${options.format}`
        a.href = canvas.toDataURL(mime)
        document.body.appendChild(a); a.click(); document.body.removeChild(a)
        this.$message.success('导出成功')
      }
      img.src = url
    },

    escapeXml(text) {
      if (!text) return ''
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;')
    },

    // 页面访问统计
    increasePageView() {
      this.request.get('/pageView/increase', {
        params: { pageName: 'flowchart' }
      }).catch(() => {});
    },

    // 加载积分配置
    async loadPointsConfig() {
      try {
        const response = await this.request.get('/ai/points-config')
        if (response.code === '200' && response.data) {
          this.pointsConfig = response.data
        }
      } catch (error) {
        console.error('加载积分配置失败:', error)
      }
    },

    // 刷新积分
    refreshPoints() {
      // 使用全局方法刷新积分
      this.$refreshPoints()
    }
  }
}
</script>

<style scoped>
.flowchart-editor { height: calc(100vh - 118px); display: flex; flex-direction: column; overflow: hidden; }
.editor-content { display: flex; height: calc(100vh - 118px); overflow: hidden; }
.element-library { width: 380px; background: #fff; display: flex; flex-direction: column; flex-shrink: 0;  z-index: 1; border-right: 1px solid #d1d5db; }
.panel-header { padding: 16px 24px; border-bottom: 1px solid #e5e7eb; flex-shrink: 0; display: flex; justify-content: space-between; align-items: center; }
.panel-title { display: flex; align-items: center; gap: 10px; font-size: 16px; font-weight: 700; color: #111827; }
.panel-icon { width: 22px; height: 22px; color: #4f46e5; }
.btn-history { display: flex; align-items: center; gap: 6px; padding: 6px 12px; font-size: 13px; color: #4f46e5; background: #fff; border: 1px solid #4f46e5; border-radius: 0; cursor: pointer; transition: all 0.2s; }
.btn-history:hover { background: #4f46e5; color: #fff; }
.btn-history svg { width: 16px; height: 16px; }
.save-reminder { font-size: 14px; color: #856404; background: #fff3cd; padding: 6px 14px; border-radius: 4px; margin: 12px 16px 0; flex-shrink: 0; }
.ai-form-panel { padding: 16px; border-bottom: 1px solid #e5e7eb; display: flex; flex-direction: column; flex: 1; overflow-y: auto; min-height: 0; }
.ai-form-panel .el-form { display: flex; flex-direction: column; flex: 1; min-height: 0; }
.ai-form-panel .el-form-item:last-child { flex: 1; display: flex; flex-direction: column; min-height: 0; }
.ai-form-panel .el-form-item:last-child .el-form-item__content { flex: 1; min-height: 0; display: flex; flex-direction: column; }
.ai-form-panel .el-form-item__label { font-size: 15px; font-weight: 500; color: #333; }
.ai-form-panel .el-input__inner, .ai-form-panel .el-textarea__inner { font-size: 14px; }
.func-editor { flex: 1; width: 100%; background: #fff; color: #333; font-family: inherit; font-size: 13px; padding: 16px; resize: none; border: 1px solid #dcdfe6; outline: none; line-height: 1.6; border-radius: 4px; box-sizing: border-box; min-height: 0; height: calc(100vh - 480px); }
.func-editor:focus { border-color: #409eff; }
.btn-generate { width: 100%; }
.panel-footer { padding: 16px 24px; border-top: 1px solid #e5e7eb; display: flex; flex-direction: column; gap: 10px; flex-shrink: 0; }
.btn-primary, .btn-secondary, .btn-ai { border-radius: 0 !important;display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 12px; font-size: 16px; font-weight: 400; cursor: pointer; border: none; transition: background 0.2s; border-radius: 4px; }
.btn-content { display: flex; align-items: center; gap: 8px; }
.btn-points { font-size: 16px; font-weight: 600; opacity: 0.9; white-space: nowrap; }
.btn-primary { background: #4f46e5; color: #fff; }
.btn-primary:hover { background: #4338ca; }
.btn-ai { background: #10b981; color: #fff; }
.btn-ai:hover { background: #059669; }
.btn-primary svg, .btn-secondary svg, .btn-ai svg { width: 22px; height: 22px; }
.btn-ai .ai-icon { width: 26px; height: 26px; object-fit: contain; }
.canvas-area { flex: 1; display: flex; flex-direction: column; overflow: hidden; background: #f9fafb; margin-left: 10px; border-left: 1px solid #d1d5db; }
.tabs-container { display: flex; align-items: center; justify-content: space-between; padding: 0 24px; background: #fff; border-bottom: 1px solid #e5e7eb; flex-shrink: 0; }
.tabs { display: flex; gap: 24px; }
.tab { display: flex; align-items: center; gap: 10px; padding: 16px 0; font-size: 16px; font-weight: 400; color: #6b7280; background: none; border: none; cursor: pointer; transition: all 0.2s; position: relative; }
.tab svg { width: 22px; height: 22px; }
.tab:hover { color: #374151; }
.tab.active { color: #4f46e5; }
.tab.active::after { content: ''; position: absolute; bottom: -1px; left: 0; right: 0; height: 2px; background: #4f46e5; }
.toolbar { display: flex; align-items: center; justify-content: flex-start; gap: 16px; padding: 10px 24px; background: #fff; border-bottom: 1px solid #e5e7eb; flex-shrink: 0; flex-wrap: wrap; }
.tip-banner { font-size: 14px; color: #856404; background: #fff3cd; padding: 6px 14px; border-radius: 4px; flex-shrink: 0; }
.canvas-actions { display: flex; gap: 10px; }
.tool-btn { display: flex; align-items: center; gap: 6px; padding: 6px 12px; font-size: 13px; color: #fff; border: none; border-radius: 4px; cursor: pointer; transition: background 0.2s; }
.tool-btn i { font-size: 14px; }
.btn-green { background: #10b981; } .btn-green:hover { background: #059669; }
.btn-teal { background: #0891b2; } .btn-teal:hover { background: #0e7490; }
.btn-orange { background: #f59e0b; } .btn-orange:hover { background: #d97706; }
.btn-blue { background: #3b82f6; } .btn-blue:hover { background: #2563eb; }
.btn-purple { background: #8c6ff7; } .btn-purple:hover { background: #7559d4; }
.params-setting { display: flex; align-items: center; gap: 15px; padding: 16px 24px; background-color: #f9fafb; border-bottom: 1px solid #e5e7eb; flex-shrink: 0; flex-wrap: wrap; }
.param-item { display: flex; align-items: center; gap: 8px; }
.param-item label { font-size: 14px; color: #374151; white-space: nowrap; font-weight: 500; }
.number-input { display: flex; align-items: center; gap: 4px; border: 1px solid #dcdfe6; border-radius: 4px; background: white; padding: 0 4px; height: 32px; }
.number-input .el-button { width: 20px; height: 20px; padding: 0; min-height: auto; border: none; background: transparent; color: #606266; font-size: 14px; }
.number-input .el-button:hover { background: #f5f7fa; color: #409eff; }
.number-value { min-width: 30px; text-align: center; font-size: 13px; color: #606266; line-height: 32px; }
.canvas-content { flex: 1; overflow: hidden; position: relative; }
.canvas { width: 100%; height: 100%; background: white; overflow: hidden; }
.diagram-svg { width: 100%; height: 100%; cursor: grab; }
.diagram-svg.panning { cursor: grabbing !important; }
.drag-area { cursor: move; }
.text-drag-area { cursor: move; }
.text-drag-hint { pointer-events: none; }
.resize-handle { cursor: pointer; }
.resize-handle:hover { fill: #40a9ff !important; }
.resize-width { cursor: ew-resize; }
.resize-height { cursor: ns-resize; }
.resize-both { cursor: nw-resize; }
.delete-btn { cursor: pointer; }
.delete-btn:hover { fill: #ff7875 !important; }
.delete-text-btn { cursor: pointer; }
.endpoint-handle { cursor: crosshair; }
.line-main { pointer-events: none; }
.line-drag-area { cursor: move; }
.node-label { font-size: 12px; fill: #333; user-select: none; cursor: pointer; }
.node-label:hover { fill: #1890ff; }
.boundary-label { font-size: 12px; fill: #333; user-select: none; cursor: pointer; }
.boundary-label:hover { fill: #1890ff; }
.boundary-label.text-dragging { fill: #1890ff; cursor: move; }
.relationship-label { font-size: 10px; fill: #666; user-select: none; }
.inline-edit-input { width: 100%; height: 24px; border: 2px solid #1890ff; border-radius: 4px; padding: 0 8px; font-size: 12px; text-align: center; background: white; outline: none; box-shadow: 0 2px 8px rgba(0,0,0,0.3); font-family: inherit; line-height: 24px; box-sizing: border-box; }
.inline-edit-input:focus { border-color: #40a9ff; }
.text-dragging { fill: #1890ff; cursor: move; }

/* 历史流程图弹框样式 */
.history-content {
  min-height: 400px;
}

.history-description {
  background: #f8f9fa;
  border-radius: 4px;
  padding: 12px 16px;
  font-size: 13px;
  color: #666;
}

.history-description ul {
  margin: 0;
  padding-left: 20px;
  list-style: none;
}

.history-description li {
  line-height: 1.8;
}

.diagram-name {
  font-weight: 500;
  color: #333;
}

.diagram-name.current {
  color: #409EFF;
  font-weight: 600;
}

.diagram-content {
  font-size: 13px;
  color: #666;
}

.absolute-time {
  font-size: 13px;
  color: #666;
}

.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.pagination-container {
  margin-top: 16px;
  display: flex;
  justify-content: center;
}
</style>
